import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { User, UserInvitation } from '../types';
import RoleGuard from '../components/RoleGuard';

const UserManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [inviteData, setInviteData] = useState<UserInvitation>({
    email: '',
    firstName: '',
    lastName: '',
    role: 'viewer',
  });
  const [editRole, setEditRole] = useState<string>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteToken, setInviteToken] = useState<string>('');
  const { company, user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const userData = await authAPI.getCompanyUsers();
      setUsers(userData);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    try {
      setError('');
      const response = await authAPI.inviteUser(inviteData);
      
      if (response.inviteToken) {
        setInviteToken(response.inviteToken);
      }
      
      setSuccess('User invitation sent successfully!');
      setInviteDialogOpen(false);
      setInviteData({ email: '', firstName: '', lastName: '', role: 'viewer' });
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send invitation');
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditRole(user.role);
    setEditDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      await authAPI.updateUserRole(selectedUser._id, editRole);
      setSuccess('User role updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setError('');
      await authAPI.deactivateUser(selectedUser._id);
      setSuccess('User deactivated successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const getRoleColor = (role: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' } = {
      super_admin: 'error',
      fleet_manager: 'primary',
      alert_manager: 'warning',
      maintenance: 'info',
      viewer: 'secondary',
    };
    return colors[role] || 'default';
  };

  const getRoleLabel = (role: string) => {
    const labels: { [key: string]: string } = {
      super_admin: 'Super Admin',
      fleet_manager: 'Fleet Manager',
      alert_manager: 'Alert Manager',
      maintenance: 'Maintenance',
      viewer: 'Viewer',
    };
    return labels[role] || role;
  };

  if (loading) {
    return <Typography>Loading users...</Typography>;
  }

  return (
    <RoleGuard roles={['super_admin', 'fleet_manager']}>
      <Box>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          mb={isMobile ? 2 : 3}
          gap={isMobile ? 1.5 : 2}
        >
          <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold">
            User Management
          </Typography>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            onClick={() => setInviteDialogOpen(true)}
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            Invite User
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Card>
          <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" gutterBottom>
              {company?.name} Team Members
            </Typography>

            {isMobile ? (
              // Mobile: Card View
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {users.map((user) => (
                  <Card key={user._id} variant="outlined">
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <RoleGuard roles={['super_admin', 'fleet_manager']}>
                            {(user.role !== 'super_admin' || currentUser?.role === 'super_admin') && (
                              <IconButton size="small" onClick={() => handleEditUser(user)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </RoleGuard>
                          
                          <RoleGuard roles="super_admin">
                            {user.role !== 'super_admin' && user._id !== currentUser?.id && (
                              <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </RoleGuard>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Role:</Typography>
                          <Chip
                            label={getRoleLabel(user.role)}
                            color={getRoleColor(user.role)}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Status:</Typography>
                          <Chip
                            label={user.isActive ? 'Active' : 'Inactive'}
                            color={user.isActive ? 'success' : 'default'}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Last Login:</Typography>
                          <Typography variant="caption">
                            {user.lastLogin
                              ? new Date(user.lastLogin).toLocaleDateString()
                              : 'Never'}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              // Desktop: Table View
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleLabel(user.role)}
                            color={getRoleColor(user.role)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.isActive ? 'Active' : 'Inactive'}
                            color={user.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {user.lastLogin
                            ? new Date(user.lastLogin).toLocaleDateString()
                            : 'Never'}
                        </TableCell>
                        <TableCell align="right">
                          <RoleGuard roles={['super_admin', 'fleet_manager']}>
                            {(user.role !== 'super_admin' || currentUser?.role === 'super_admin') && (
                              <Tooltip title="Edit User">
                                <IconButton size="small" onClick={() => handleEditUser(user)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </RoleGuard>
                          
                          <RoleGuard roles="super_admin">
                            {user.role !== 'super_admin' && user._id !== currentUser?.id && (
                              <Tooltip title="Deactivate User">
                                <IconButton size="small" color="error" onClick={() => handleDeleteUser(user)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </RoleGuard>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Invite User Dialog */}
        <Dialog 
          open={inviteDialogOpen} 
          onClose={() => setInviteDialogOpen(false)}
          fullScreen={isMobile}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant={isMobile ? 'h6' : 'h5'}>Invite New User</Typography>
              {isMobile && (
                <IconButton onClick={() => setInviteDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="First Name"
                value={inviteData.firstName}
                onChange={(e) =>
                  setInviteData({ ...inviteData, firstName: e.target.value })
                }
                margin="normal"
                size={isMobile ? 'small' : 'medium'}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={inviteData.lastName}
                onChange={(e) =>
                  setInviteData({ ...inviteData, lastName: e.target.value })
                }
                margin="normal"
                size={isMobile ? 'small' : 'medium'}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={inviteData.email}
                onChange={(e) =>
                  setInviteData({ ...inviteData, email: e.target.value })
                }
                margin="normal"
                size={isMobile ? 'small' : 'medium'}
              />
              <FormControl fullWidth margin="normal" size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={inviteData.role}
                  onChange={(e) =>
                    setInviteData({ ...inviteData, role: e.target.value as any })
                  }
                  label="Role"
                >
                  <MenuItem value="fleet_manager">Fleet Manager</MenuItem>
                  <MenuItem value="alert_manager">Alert Manager</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            p: 2,
            '& > button': {
              width: isMobile ? '100%' : 'auto'
            }
          }}>
            <Button onClick={() => setInviteDialogOpen(false)} fullWidth={isMobile} variant={isMobile ? 'outlined' : 'text'}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleInviteUser} fullWidth={isMobile}>
              Send Invitation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Role Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={() => setEditDialogOpen(false)}
          fullScreen={isMobile}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant={isMobile ? 'h6' : 'h5'}>Edit User Role</Typography>
              {isMobile && (
                <IconButton onClick={() => setEditDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" gutterBottom>
                User: {selectedUser?.firstName} {selectedUser?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email: {selectedUser?.email}
              </Typography>
              <FormControl fullWidth margin="normal" size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  label="Role"
                >
                  <MenuItem value="fleet_manager">Fleet Manager</MenuItem>
                  <MenuItem value="alert_manager">Alert Manager</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="viewer">Viewer</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0,
            p: 2,
            '& > button': {
              width: isMobile ? '100%' : 'auto'
            }
          }}>
            <Button onClick={() => setEditDialogOpen(false)} fullWidth={isMobile} variant={isMobile ? 'outlined' : 'text'}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleUpdateRole} fullWidth={isMobile}>
              Update Role
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => setDeleteDialogOpen(false)}
          fullScreen={isMobile}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant={isMobile ? 'h6' : 'h5'}>Deactivate User</Typography>
              {isMobile && (
                <IconButton onClick={() => setDeleteDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to deactivate {selectedUser?.firstName} {selectedUser?.lastName}?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will prevent them from logging in, but their data will be preserved.
            </Typography>
          </DialogContent>
          <DialogActions sx={{
            flexDirection: isMobile ? 'column-reverse' : 'row',
            gap: isMobile ? 1 : 0,
            p: 2,
            '& > button': {
              width: isMobile ? '100%' : 'auto'
            }
          }}>
            <Button onClick={() => setDeleteDialogOpen(false)} fullWidth={isMobile} variant={isMobile ? 'outlined' : 'text'}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={confirmDeleteUser} fullWidth={isMobile}>
              Deactivate User
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </RoleGuard>
  );
};

export default UserManagementPage;